import { SessionQuery } from './../../../../core/state/session/session.query';
import { SbConfirmDialogService } from './../../../sb-confirm-dialog/sb-confirm-dialog.service';
import { ToastService } from './../../../../shared/services/toast/toast.service';
import { transformModel } from './../../../../shared/utils';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { SbRoutingButton, SbMenuItem, SbRequestMethod } from '@models';
import { SbDataService } from '../../../services';
import { GridApi, RowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { SbAgTableButtonCellRendererParams } from './sb-ag-table-button-cell-renderer-params';
import { AmendService } from 'src/app/shared/services/amend.service';

@Component({
    selector: 'app-sb-ag-table-button-renderer',
    template: `
        <div class="button-wrapper">
            <span *ngIf="displayButton">
                <button class="btn secondary" (click)="buttonClicked()">
                    {{ label }}
                </button>
            </span>
        </div>
    `,
    styleUrls: ['./sb-ag-table-button-renderer.component.scss']
})
export class SbAgTableButtonRenderer {
    menuItem: SbMenuItem;
    button: SbRoutingButton;
    label: string;
    data: any;
    gridApi: GridApi;
    submitSubscription: Subscription;
    rowNode: RowNode;
    displayButton: boolean = true;

    constructor(
        private http: HttpClient,
        private dataService: SbDataService,
        private sessionQuery: SessionQuery,
        public confirmDialogService: SbConfirmDialogService,
        private toastService: ToastService,
        private _amendService: AmendService
    ) {}

    // gets called once before the renderer is used
    agInit(params: SbAgTableButtonCellRendererParams): void {
        this.button = params.button;
        this.menuItem = params.menuItem;
        this.label = params.menuItem?.name;
        this.gridApi = params.api;
        this.data = params.data;
        this.rowNode = params.node;
        this.displayButton = this.hasAccessToAction(params.menuItem, params.node);
    }

    hasAccessToAction(menuItem: SbMenuItem, rowNode: RowNode): boolean {
        let expressionGuard = true;
        if (menuItem.guard) {
            const f = new Function('data', menuItem.guard);
            expressionGuard = f(rowNode.data);
        }
        return menuItem.accessRight
            ? this.sessionQuery.hasScopeAccess(menuItem.accessRight) && expressionGuard
            : expressionGuard;
    }

    // gets called whenever the cell refreshes
    refresh(params: SbAgTableButtonCellRendererParams) {
        return false;
    }

    /**
     * Essentially ripped the funcionality/logic for menuItems for this since we're moving away from
     * obscure untyped / generic code. Until then, reusing contextMenuItem logic.
     */
    buttonClicked() {
        // Redirect to form page when clicking the amend button (only applicable for some)
        if (this.menuItem.action.path.httpMethod === 'AMEND') {
            this._amendService.redirectToForm(this.data, [this.data]);
            return;
        }
        this.confirmDialogService
            .confirmDialog({
                title: 'Confirmation',
                message: `Please confirm your action, this action can't be undone.`,
                cancelText: `No`,
                confirmText: `Yes`
            })
            .subscribe((res) => {
                if (res) {
                    this.performAction();
                }
            });
    }

    private performAction() {
        const path = this.menuItem.action.path;

        const requestParams = transformModel(
            this.data,
            false,
            this.menuItem.action.requestMapping
        );

        this.http
            .request(path.httpMethod, this.dataService.getResourceUrl(path), {
                params: requestParams
            })
            .subscribe(
                (response) => {
                    (<any[]>response).forEach((rowData) => {
                        if (path.httpMethod === SbRequestMethod.DELETE) {
                            this.gridApi.updateRowData({ remove: [rowData] });
                        } else {
                            this.rowNode.setData(rowData);
                            this.gridApi.flashCells({
                                rowNodes: [this.rowNode]
                            });
                        }
                    });
                    this.toastService.show({
                        intent: 'success',
                        message: 'Your action completed successfully.',
                        title: 'Action Successful'
                    });
                },
                () => {
                    this.toastService.show({
                        intent: 'critical',
                        message:
                            'Your action was not successful. Please try again or investigate inconsistencies.',
                        title: 'Action Failed'
                    });
                }
            );
    }

    getValueToDisplay(params: SbAgTableButtonCellRendererParams) {
        return params.valueFormatted ? params.valueFormatted : params.value;
    }
}
